import React from 'react';

import type { StoryboardPromotion } from '@/store/slices/creativeJobs';

import type { LocalizedCopy } from '../../ad-language';
import { localizeAdCopy } from '../../ad-language-api';
import { adSubjectFromItem } from '../../ad-subjects';
import type { AdSubject } from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import type { VideoSubjectType } from '../../video-subjects';

/** The answers read this page session, by tenant, language and banner id: a composer remounted (easy <-> advanced, a
 *  reopen) finds its promotions' copy at once. Only answered reads are kept; a failed one is read again. */
const answers = new Map<string, LocalizedCopy>();
/** The reads in flight by the same key, so two mounts share one request. */
const inFlight = new Map<string, Promise<LocalizedCopy | null>>();

const SEP = '\u0000';
const keyOf = (tenantId: number, language: string, id: string): string => [tenantId, language, id].join(SEP);

/** One promotion's copy in `language` (the shared answer when there is one). null when the read failed. */
const readCopy = (tenantId: number, language: string, subject: AdSubject): Promise<LocalizedCopy | null> => {
  const key = keyOf(tenantId, language, subject.id);
  const known = answers.get(key);
  if (known) return Promise.resolve(known);
  const pending = inFlight.get(key);
  if (pending) return pending;
  const request = localizeAdCopy(tenantId, { language, subjectType: 'promotion', subject }).then((res) => {
    inFlight.delete(key);
    if (!res.ok) return null;
    answers.set(key, res.data);
    return res.data;
  });
  inFlight.set(key, request);
  return request;
};

/** The subject a promotion is localized from: its pool item when the brand still lists it (the creatives list's
 *  English-first copy, the same request on every open, so the service's cache answers a reopen), else the promotion as
 *  the ad holds it. */
const subjectOf = (p: StoryboardPromotion, items: CreativeItem[]): AdSubject => {
  const item = items.find((i) => i.banner_id === p.banner_id);
  if (item) return adSubjectFromItem(item);
  return {
    id: p.banner_id,
    name: p.headline ?? '',
    image_url: p.image_url,
    headline: p.headline ?? '',
    subtitle: p.subtitle ?? '',
    cta_text: p.cta_text ?? '',
    cta_href: '',
  };
};

export interface UseLocalizedPromotionsArgs {
  tenantId: number | undefined;
  /** The ad's language; '' or undefined = none (an older ad: nothing to localize). */
  language: string | undefined;
  subjectType: VideoSubjectType;
  /** The ad's promotions (only those with a banner id are localized). */
  promotions: StoryboardPromotion[];
  /** The brand's banner pool (the promotions' source copy). */
  items: CreativeItem[];
  /** False while the ad cannot take the copy (a rendered, locked version): nothing is read and nothing waits. */
  enabled: boolean;
}

export interface LocalizedPromotionsState {
  /** `idle`: nothing to localize (no language, not a promotion ad, disabled). `ready`: every promotion has its copy. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Each answered promotion's copy in the language, by banner id. */
  copies: Readonly<Record<string, LocalizedCopy>>;
  /** Lumio translated some of it (the website has none in this language). */
  translated: boolean;
  /** Reads the failed promotions again. */
  retry: () => void;
}

/**
 * A promotion video ad's promotions' copy in the ad's language, for the composer to sync into its draft
 * (localizePromotionsCopy, an automatic fill that never forks). Each promotion is read once per language with
 * `ad-copy-localize`: the promotion record's own text in the language, else Lumio's faithful translation. Games and
 * gamification features have nothing to read (their headline is a name and their call to action the per-language
 * default), so a non-promotion ad is `idle`.
 */
export const useLocalizedPromotions = ({
  tenantId,
  language,
  subjectType,
  promotions,
  items,
  enabled,
}: UseLocalizedPromotionsArgs): LocalizedPromotionsState => {
  const lang = (language ?? '').trim();
  const active = Boolean(enabled && tenantId && lang && subjectType === 'promotion');
  // The banner ids to localize, each once, as one stable key.
  const idsKey = active
    ? Array.from(new Set(promotions.map((p) => p.banner_id).filter((id) => id !== ''))).join(SEP)
    : '';
  const promotionsRef = React.useRef(promotions);
  promotionsRef.current = promotions;
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  const [attempt, setAttempt] = React.useState(0);
  // Bumped when this mount's reads answer, so the module answers are read again.
  const [landed, setLanded] = React.useState(0);
  // The ids whose read failed, for one (tenant, language, ids, attempt): a changed pick or a retry starts clean.
  const readKey = [tenantId ?? 0, lang, idsKey, attempt].join(SEP);
  const [failed, setFailed] = React.useState<{ key: string; ids: string[] } | null>(null);

  // Syncs with the network: reads the copy of every promotion that has none in this language yet.
  React.useEffect(() => {
    if (!tenantId || !lang || !idsKey) return undefined;
    const missing = idsKey.split(SEP).filter((id) => !answers.has(keyOf(tenantId, lang, id)));
    if (missing.length === 0) return undefined;
    let live = true;
    const reads = missing.map((id) => {
      const p = promotionsRef.current.find((x) => x.banner_id === id);
      return p ? readCopy(tenantId, lang, subjectOf(p, itemsRef.current)) : Promise.resolve(null);
    });
    void Promise.all(reads).then((results) => {
      if (!live) return;
      const bad = missing.filter((_id, i) => results[i] === null);
      setFailed(bad.length > 0 ? { key: readKey, ids: bad } : null);
      setLanded((n) => n + 1);
    });
    return () => {
      live = false;
    };
  }, [tenantId, lang, idsKey, readKey]);

  const retry = React.useCallback((): void => setAttempt((n) => n + 1), []);

  const copies = React.useMemo<Record<string, LocalizedCopy>>(() => {
    const out: Record<string, LocalizedCopy> = {};
    if (!tenantId || !idsKey) return out;
    idsKey.split(SEP).forEach((id) => {
      const copy = answers.get(keyOf(tenantId, lang, id));
      if (copy) out[id] = copy;
    });
    return out;
    // `landed` re-reads the module answers once this mount's reads have answered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, lang, idsKey, landed]);

  const wanted = idsKey ? idsKey.split(SEP).length : 0;
  const answered = Object.keys(copies).length;
  const status: LocalizedPromotionsState['status'] =
    !active || wanted === 0
      ? 'idle'
      : answered === wanted
        ? 'ready'
        : failed?.key === readKey && failed.ids.some((id) => !copies[id])
          ? 'error'
          : 'loading';
  const translated = Object.values(copies).some((c) => c.translated.length > 0);

  return React.useMemo(() => ({ status, copies, translated, retry }), [status, copies, translated, retry]);
};

export default useLocalizedPromotions;
